/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { ViewModel } from "../../viewmodel/viewmodel"

import type { ApkDetailService } from "./apk-detail-service"
import type { ApkDetailStore } from "./apk-detail-store"
import type { ApkDetail } from "./apk-detail-types"

/**
 * View model for one APK detail page.
 *
 * Scoped to a single APK ID; each APK gets its own view-model instance and
 * store, so their details cannot overwrite each other.
 *
 * The container keeps the instance for the session. Returning to the page
 * refreshes the detail behind the route spinner; if the request fails, the
 * previously loaded detail remains available.
 */
export class ApkDetailViewModel implements ViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly apkId: number,
        private readonly store: ApkDetailStore,
        private readonly service: ApkDetailService
    ) {}

    /**
     * Loaded APK detail, or `null` until the first successful load.
     *
     * Failed loads leave existing detail intact.
     */
    get apkDetail(): ApkDetail | null {
        return this.store.detail
    }

    /**
     * Loads the APK detail into the store.
     *
     * The service reports failures to the logger, so callers do not need to
     * handle the request result.
     */
    async getApkDetail(): Promise<void> {
        await this.service.getApkDetail(this.apkId)
    }
}
